/*
*   This content is licensed according to the W3C Software License at
*   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
*/



/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */

import { forEach } from 'lodash';
import PopupMenuItem from './popup-menu-item-links';

/* == IMPORTS =============================================================== */



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */

const
    moduleName = 'popup-menu-links',
    baseCssClass = `js-${moduleName}`,

    selectors = {
        base: `.${baseCssClass}`
    };

/* == PRIVATE VARIABLES ===================================================== */

/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */

export default class PopupMenu {
    constructor(baseElement, controllerObj) {
        const msgPrefix = 'PopupMenu constructor argument domNode ';

        // Check whether baseElement is a DOM element
        if (!baseElement instanceof Element) {
            throw new TypeError(msgPrefix + 'is not a DOM Element.');
        }
        // Check whether baseElement has child elements
        if (baseElement.childElementCount === 0) {
            throw new Error(msgPrefix + 'has no element children.');
        }
        // Check whether baseElement descendant elements have A elements
        let childElement = baseElement.firstElementChild;
        while (childElement) {
            var menuitem = childElement.firstElementChild;
            if (menuitem && menuitem === 'A') {
                throw new Error(msgPrefix + 'has descendant elements that are not A elements.');
            }
            childElement = childElement.nextElementSibling;
        }

        this.isMenubar = false;

        this.baseElement = baseElement;
        this.controller = controllerObj;

        this.menuitems = []; // See PopupMenu init method
        this.firstChars = []; // See PopupMenu init method

        this.firstItem = null; // See PopupMenu init method
        this.lastItem = null; // See PopupMenu init method

        this.hasFocus = false; // See MenuItem handleFocus, handleBlur
        this.hasHover = false; // See PopupMenu handleMouseover, handleMouseout

        this.init();
    }

    /* MENU DISPLAY METHODS */

    Open() {
        // Get position and bounding rectangle of controller object's DOM node
        const rect = this.controller.baseElement.getBoundingClientRect();

        // Set CSS properties
        if (!this.controller.isMenubarItem) {
            this.baseElement.parentNode.style.position = 'relative';
            this.baseElement.style.display = 'block';
            this.baseElement.style.position = 'absolute';
            this.baseElement.style.left = rect.width + 'px';
            this.baseElement.style.zIndex = 100;
        }
        else {
            this.baseElement.style.display = 'block';
            this.baseElement.style.position = 'absolute';
            this.baseElement.style.top = (rect.height - 1) + 'px';
            this.baseElement.style.zIndex = 100;
        }

        this.controller.setExpanded(true);

    };

    Close(force) {

        let controllerHasHover = this.controller.hasHover,
            hasFocus = this.hasFocus;

        forEach(this.menuitems, (menuItem) => {
            if (menuItem.popupMenu) {
                hasFocus = hasFocus | menuItem.popupMenu.hasFocus;
            }
        })
        
        if (!this.controller.isMenubarItem) {
            controllerHasHover = false;
        }

        if (force || (!hasFocus && !this.hasHover && !controllerHasHover)) {
            this.baseElement.style.display = 'none';
            this.baseElement.style.zIndex = 0;
            this.controller.setExpanded(false);
        }
    };

    /* FOCUS MANAGEMENT METHODS */

    setFocusToController(command, flag) {

        if (typeof command !== 'string') {
            command = '';
        }

        function setFocusToMenubarItem(controller, close) {
            while (controller) {
                if (controller.isMenubarItem) {
                    controller.baseElement.focus();
                    return controller;
                }
                else {
                    if (close) {
                        controller.menu.Close(true);
                    }
                    controller.hasFocus = false;
                }
                controller = controller.menu.controller;
            }
            return false;
        }

        if (command === '') {
            if (this.controller && this.controller.baseElement) {
                this.controller.baseElement.focus();
            }
            return;
        }

        if (!this.controller.isMenubarItem) {
            this.controller.baseElement.focus();
            this.Close();

            if (command === 'next') {
                var menubarItem = setFocusToMenubarItem(this.controller, false);
                if (menubarItem) {
                    menubarItem.menu.setFocusToNextItem(menubarItem, flag);
                }
            }
        }
        else {
            if (command === 'previous') {
                this.controller.menu.setFocusToPreviousItem(this.controller, flag);
            }
            else if (command === 'next') {
                this.controller.menu.setFocusToNextItem(this.controller, flag);
            }
        }

    };

    setFocusToFirstItem() {
        this.firstItem.baseElement.focus();
    };

    setFocusToLastItem() {
        this.lastItem.baseElement.focus();
    };

    setFocusToPreviousItem(currentItem) {
        let index;

        if (currentItem === this.firstItem) {
            this.lastItem.baseElement.focus();
        }
        else {
            index = this.menuitems.indexOf(currentItem);
            this.menuitems[index - 1].baseElement.focus();
        }
    };

    setFocusToNextItem(currentItem) {
        let index;

        if (currentItem === this.lastItem) {
            this.firstItem.baseElement.focus();
        }
        else {
            index = this.menuitems.indexOf(currentItem);
            this.menuitems[index + 1].baseElement.focus();
        }
    };

    setFocusByFirstCharacter(currentItem, char) {
        let start, index;

        char = char.toLowerCase();

        // Get start index for search based on position of currentItem
        start = this.menuitems.indexOf(currentItem) + 1;
        if (start === this.menuitems.length) {
            start = 0;
        }

        // Check remaining slots in the menu
        index = this.getIndexFirstChars(start, char);

        // If not found in remaining slots, check from beginning
        if (index === -1) {
            index = this.getIndexFirstChars(0, char);
        }

        // If match was found...
        if (index > -1) {
            this.menuitems[index].baseElement.focus();
        }
    };

    getIndexFirstChars(startIndex, char) {
        for (let i = startIndex; i < this.firstChars.length; i++) {
            if (char === this.firstChars[i]) {
                return i;
            }
        }
        return -1;
    };

    /* EVENT HANDLERS */

    handleMouseover(event) {
        this.hasHover = true;
    };

    handleMouseout(event) {
        this.hasHover = false;
        setTimeout(this.Close.bind(this, false), 1);
    };

    /* Initializer */

    init() {
        let childElement, menuElement, menuItem, textContent, numItems;

        // Configure the baseElement itself

        this.baseElement.addEventListener('mouseover', this.handleMouseover.bind(this));
        this.baseElement.addEventListener('mouseout', this.handleMouseout.bind(this));

        // Traverse the element children of baseElement: configure each with
        // menuitem role behavior and store reference in menuitems array.
        childElement = this.baseElement.firstElementChild;

        while (childElement) {
            menuElement = childElement.firstElementChild;

            if (menuElement && menuElement.tagName === 'A') {
                menuItem = new PopupMenuItem(menuElement, this);
                menuItem.init();
                this.menuitems.push(menuItem);
                textContent = menuElement.textContent.trim();
                this.firstChars.push(textContent.substring(0, 1).toLowerCase());
            }
            childElement = childElement.nextElementSibling;
        }

        // Use populated menuitems array to initialize firstItem and lastItem.
        numItems = this.menuitems.length;
        if (numItems > 0) {
            this.firstItem = this.menuitems[0];
            this.lastItem = this.menuitems[numItems - 1];
        }
    }
}